import React, { useState } from "react";

/**
 * Lottery logo with TRUE transparent background.
 * Uses an SVG color matrix filter that converts black pixels to transparent
 * (alpha set from luminance), so the PNG's black background disappears.
 */
export default function LotteryLogo({ lottery, size = 56, draw = null }) {
  const [imgError, setImgError] = useState(false);
  if (!lottery && !draw) return null;

  const logoUrl = (draw && draw.logo) || (lottery && lottery.logo);

  if (logoUrl && !imgError) {
    // Render the underlying image at 3x the display size so the browser
    // decodes it at high resolution and downscales sharply. We use a clean
    // white bubble background instead of an aggressive luminance filter,
    // because that filter was destroying edge detail on these brand logos.
    const intrinsic = Math.round(size * 3);
    return (
      <div
        className="relative flex items-center justify-center flex-shrink-0 rounded-full overflow-hidden bg-white"
        style={{ width: size, height: size, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}
      >
        <img
          src={logoUrl}
          alt={(draw && draw.name) || (lottery && lottery.name) || "Lotería"}
          loading="lazy"
          decoding="async"
          width={intrinsic}
          height={intrinsic}
          className="object-contain"
          style={{
            width: "92%",
            height: "92%",
            imageRendering: "auto",
            transform: "translate3d(0,0,0)",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  const [c1, c2] = lottery?.colors || ["#1d6cb1", "#0e4a7a"];
  const initials = lottery?.initials || lottery?.short?.slice(0, 2) || "LO";
  const text = lottery?.text || "#fff";

  return (
    <div
      className="relative rounded-2xl flex items-center justify-center font-extrabold shadow-md overflow-hidden flex-shrink-0"
      style={{
        width: size,
        height: size,
        background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`,
        color: text,
      }}
    >
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 25% 20%, rgba(255,255,255,0.6) 0%, transparent 35%), radial-gradient(circle at 80% 80%, rgba(0,0,0,0.35) 0%, transparent 40%)",
        }}
      />
      <span
        className="relative tracking-tight"
        style={{ fontSize: size * 0.36, fontFamily: "'Playfair Display', serif" }}
      >
        {initials}
      </span>
      <span
        className="absolute bottom-1 right-1 text-[8px] uppercase tracking-widest opacity-70 font-bold"
        style={{ color: text }}
      >
        RD
      </span>
    </div>
  );
}

/**
 * SVG filter that maps each pixel's luminance to its alpha value.
 * Pure black -> alpha 0 (fully transparent). Mount once in the page.
 */
export function LogoFilterDefs() {
  return (
    <svg
      width="0"
      height="0"
      style={{ position: "absolute", width: 0, height: 0 }}
      aria-hidden="true"
    >
      <defs>
        <filter id="np-remove-black" colorInterpolationFilters="sRGB">
          {/* Compute luminance and put it into alpha; keep RGB intact */}
          <feColorMatrix
            type="matrix"
            values="1 0 0 0 0
                    0 1 0 0 0
                    0 0 1 0 0
                    0.299 0.587 0.114 0 0"
          />
          {/* Boost contrast on alpha so near-black is fully transparent and
              the rest stays opaque. */}
          <feComponentTransfer>
            <feFuncA type="linear" slope="3" intercept="-0.1" />
          </feComponentTransfer>
        </filter>
        {/* INVERTED: pure white -> alpha 0 (transparent), keep colored content. */}
        <filter id="np-remove-white" colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="1 0 0 0 0
                    0 1 0 0 0
                    0 0 1 0 0
                    -0.299 -0.587 -0.114 0 1"
          />
          <feComponentTransfer>
            <feFuncA type="linear" slope="4" intercept="-0.05" />
          </feComponentTransfer>
        </filter>
      </defs>
    </svg>
  );
}
